import { parseISO } from 'date-fns';
import { clone, cloneDeep } from 'es-toolkit';
import { CountryCode } from 'libphonenumber-js';
import { action, computed, makeObservable, observable } from 'mobx';
import { Award, AwardNominationSummary, blankNominationForStaff, Country, CountryNominationSummary, Document, DocumentMetadata, Judge, Judgement, JudgementStatus, Nomination, NominationForStaff, NominationRef, NominationRole, Organization, OrganizationType, Owner, ProgramDate, Reviewer, ReviewerData, ScrubbingStatusType, UploadedMedia, User } from '../../models';
import {
  AssociatedCountriesRequest,
  associatedCountriesRequestSchema,
} from '../../models/schemas/associated-countries-request.schema';
import {
  AdditionalContactValidation,
  additionalContactValidationSchema,
  CustomerReferenceValidation,
  customerReferenceValidationSchema,
  JointNomineeValidation,
  jointNomineeValidationSchema,
  NominationOwnerEmailValidation,
  nominationOwnerEmailValidationSchema,
  ownerValidationSchema,
} from '../../models/schemas/nomination-validation.schema';
import {
  CompanyInformationValidation,
  companyInformationValidationSchema,
  NominationSubmissionValidation,
  nominationSubmissionValidationSchema,
  NominationValidationInput,
  YourInformationValidation,
} from '../../models/schemas/nomination-wizard-validation.schema';
import { createValidationState, ValidationState } from '../../shared/validation';
import { PhoneNumberField, PhoneNumberFieldId } from '../shared-components/phone-number-input';

type ModalType = 'add' | 'update' | undefined;

const MAX_COLLAPSED_WRITTEN_EXECUTIVE_SUMMARY_LENGTH = 600;

export class NominationForStaffPageUiStore {
  @observable selectedAward?: Award;

  @observable nominationForStaff: NominationForStaff = blankNominationForStaff();

  @observable isEditingMode: boolean = false;

  @observable isBeingValidated: boolean = false;

  @observable isLoading: boolean = false;

  @observable nominationForStaffOrganization?: Organization;

  @observable nominationJudgementCount: number = 0;

  @observable nominationJudgement?: Judgement;

  @observable assignedJudge?: Judge;

  @observable assignedJudges: Judge[] = [];

  @observable selectedJudgeUserId?: number;

  @observable judgementStatus?: JudgementStatus;

  @observable availableJudges: User[] = [];

  @observable writtenExecutiveSummaryContent?: string;

  @observable executiveSummaryDocument?: DocumentMetadata;

  @observable isNominationSubmissionExpanded: boolean = false;

  @observable nominationRole?: NominationRole;

  @observable isScrubbingPassedModalVisible: boolean = false;

  @observable isScrubbingDisqualifiedModalVisible: boolean = false;

  @observable scrubbingStatus?: ScrubbingStatusType = undefined;

  @observable uploadedMedia: UploadedMedia[] = [];

  @observable isDeleteExecutiveSummaryFile: boolean = false;

  @observable supportingDocumentIdsToDelete: number[] = [];

  @observable documentToUpload: Document[] = [];

  @observable sortedAwardNominationSummaries: AwardNominationSummary[] = [];

  @observable sortedAwardNominationRefs: NominationRef[] = [];

  @observable sortedCountryNominationSummaries: CountryNominationSummary[] = [];

  @observable sortedCountryNominationRefs: NominationRef[] = [];

  @observable staffProgramDates: ProgramDate[] = [];

  @observable currentReviewer?: Reviewer;

  @observable availableNominationReviewers: Reviewer[] = [];

  @observable selectedReviewerEmailAddress?: string;

  @observable countryCodeMapById: Map<PhoneNumberFieldId, CountryCode> = new Map();

  @observable editAssociatedCountryModalType?: ModalType;

  @observable editingAssociatedCountries: Country[] = [];

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.selectedAward = undefined;
    this.nominationForStaff = blankNominationForStaff();
    this.isEditingMode = false;
    this.isBeingValidated = false;
    this.isLoading = false;
    this.nominationForStaffOrganization = undefined;
    this.nominationJudgementCount = 0;
    this.writtenExecutiveSummaryContent = undefined;
    this.executiveSummaryDocument = undefined;
    this.isNominationSubmissionExpanded = false;
    this.nominationRole = undefined;
    this.isScrubbingPassedModalVisible = false;
    this.isScrubbingDisqualifiedModalVisible = false;
    this.scrubbingStatus = undefined;
    this.uploadedMedia = [];
    this.sortedAwardNominationRefs = [];
    this.sortedAwardNominationSummaries = [];
    this.sortedCountryNominationRefs = [];
    this.sortedCountryNominationSummaries = [];
    this.currentReviewer = undefined;
    this.availableNominationReviewers = [];
    this.selectedReviewerEmailAddress = undefined;
    this.staffProgramDates = [];
    this.isDeleteExecutiveSummaryFile = false;
    this.supportingDocumentIdsToDelete = [];
    this.documentToUpload = [];
    this.countryCodeMapById.clear();
    this.editAssociatedCountryModalType = undefined;
    this.editingAssociatedCountries = [];
    this.nominationJudgement = undefined;
    this.assignedJudge = undefined;
    this.assignedJudges = [];
    this.selectedJudgeUserId = undefined;
    this.judgementStatus = undefined;
    this.availableJudges = [];
  }

  @action
  setSelectedAward(selectedAward: Award): void {
    this.selectedAward = selectedAward;
  }

  @action
  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  @action
  setNominationJudgementCount(nominationJudgementCount: number): void {
    this.nominationJudgementCount = nominationJudgementCount;
  }

  @action
  setSortedAwardNominationSummaries(sortedAwardNominationSummaries: AwardNominationSummary[]): void {
    this.sortedAwardNominationSummaries = sortedAwardNominationSummaries;
  }

  @action
  setSortedAwardNominationRefs(sortedAwardNominationRefs: NominationRef[]): void {
    this.sortedAwardNominationRefs = sortedAwardNominationRefs;
  }

  @action
  setSortedCountryNominationSummaries(sortedCountryNominationSummaries: CountryNominationSummary[]): void {
    this.sortedCountryNominationSummaries = sortedCountryNominationSummaries;
  }

  @action
  setSortedCountryNominationRefs(sortedCountryNominationRefs: NominationRef[]): void {
    this.sortedCountryNominationRefs = sortedCountryNominationRefs;
  }

  @action
  setNominationRole(nominationRole?: NominationRole): void {
    const intialValue: NominationRole = {
      nominationId: 0,
      role: 'Undesignated',
    };

    if (!nominationRole) {
      this.nominationRole = intialValue;
      return;
    }

    this.nominationRole = nominationRole;
  }

  @action
  setScrubbingStatus(scrubbingStatus?: ScrubbingStatusType): void {
    this.scrubbingStatus = scrubbingStatus;
  }

  @action
  setUploadedMedia(uploadedMedia: UploadedMedia[]): void {
    this.uploadedMedia = uploadedMedia;
  }

  @action
  setStaffProgramDates(staffProgramDates: ProgramDate[]): void {
    this.staffProgramDates = staffProgramDates;
  }

  @action
  setNominationJudgement(nominationJudgement: Judgement) {
    this.nominationJudgement = nominationJudgement;
  }

  @action
  setNominationIsCaseStudy(isCaseStudy: boolean): void {
    this.nominationForStaff.isCaseStudy = isCaseStudy;
  }

  @action
  setJudgementQuestionRating(questionId: number, rating: number): void {
    const question = this.nominationJudgement?.questions.find(
      (judgementQuestion) => judgementQuestion.questionId === questionId,
    );
    if (!question) return;

    question.rating = rating;
  }

  @action
  setJudgementFeedback(feedback: string): void {
    if (!this.nominationJudgement) return;

    this.nominationJudgement.feedback = feedback;
  }

  @action
  setAssignedJudge(assignedJudge?: Judge): void {
    this.assignedJudge = assignedJudge;
  }

  @action
  setAssignedJudges(assignedJudges: Judge[]): void {
    this.assignedJudges = assignedJudges;
  }

  @action
  setSelectedJudgeUserId(selectedJudgeUserId?: number): void {
    this.selectedJudgeUserId = selectedJudgeUserId;
  }

  @action
  setJudgementStatus(judgementStatus?: JudgementStatus): void {
    this.judgementStatus = judgementStatus;
  }

  @action
  setAvailableJudges(availableJudges: User[]): void {
    this.availableJudges = availableJudges;
  }

  @action
  resetNominationJudging(): void {
    this.assignedJudge = undefined;
    this.judgementStatus = undefined;
  }

  @computed
  get selectedAssignedJudgeUser(): User | undefined {
    return this.assignedJudges.find((judge) => judge.user.userId === this.selectedJudgeUserId)?.user;
  }

  getSelectedAssignedJudgeUserId(currentUserId?: number): number | undefined {
    const currentUserJudge = this.assignedJudges.find((judge) => judge.user.userId === currentUserId);
    return currentUserJudge?.user.userId ?? this.assignedJudges.at(0)?.user.userId;
  }

  isAssignedJudge(currentUserId?: number): boolean {
    return this.assignedJudge?.user.userId === currentUserId;
  }

  getAvailableJudgeByEmail(emailAddress: string): User | undefined {
    return this.availableJudges.find((judge) => judge.emailAddress === emailAddress);
  }

  @computed
  get hasAssignedJudge(): boolean {
    return this.assignedJudge !== undefined;
  }

  @computed
  get isJudgementSubmitted(): boolean {
    return this.judgementStatus?.status === 'Submitted';
  }

  @computed
  get isScrubbingWindowAlreadyOpenButWinnersWindowNotStartYet(): boolean {
    const scrubbingWindow = this.staffProgramDates.find((date) => date.dateTypeCode === 'Scrubbing-Window');
    if (!scrubbingWindow) return false;

    const today = new Date();
    const scrubbingStartDate = parseISO(scrubbingWindow.startDate);

    if (today < scrubbingStartDate) return false;
    const winnersWindow = this.staffProgramDates.find((date) => date.dateTypeCode === 'Winners-Window');
    if (!winnersWindow) return true;

    const winnersStartDate = parseISO(winnersWindow.startDate);
    return today < winnersStartDate;
  }

  @action
  setCurrentReviewer(reviewer?: Reviewer): void {
    this.currentReviewer = reviewer;
  }

  @action
  setAvailableNominationReviewers(reviewers: Reviewer[]): void {
    this.availableNominationReviewers = reviewers;
    this.selectedReviewerEmailAddress ??= reviewers.at(0)?.emailAddress;
  }

  @action
  setSelectedReviewerEmailAddress(emailAddress?: string): void {
    this.selectedReviewerEmailAddress = emailAddress;
  }

  @action
  selectNominationReviewer(emailAddress: string): void {
    const reviewer = this.availableNominationReviewers.find(
      (availableReviewer) => availableReviewer.emailAddress.toLowerCase() === emailAddress.toLowerCase(),
    );
    if (!reviewer) return;

    this.selectedReviewerEmailAddress = reviewer.emailAddress;
  }

  @action
  resetNominationReviewerModal(): void {
    this.availableNominationReviewers = [];
    this.selectedReviewerEmailAddress = undefined;
  }

  @computed
  get reviewerData(): ReviewerData | undefined {
    if (!this.selectedReviewerEmailAddress) return undefined;

    return {
      emailAddress: this.selectedReviewerEmailAddress,
    };
  }

  isSelectedReviewer(reviewer?: Reviewer): boolean {
    return this.selectedReviewerEmailAddress?.toLowerCase() === reviewer?.emailAddress.toLowerCase();
  }

  @action
  setSelectedCountryCode(id: PhoneNumberFieldId, countryCode: CountryCode): void {
    if (this.countryCodeMapById.has(id)) this.countryCodeMapById.delete(id);
    this.countryCodeMapById.set(id, countryCode);
  }

  @action
  setDeleteExecutiveSummaryFile(isDeleteExecutiveSummaryFile: boolean): void {
    this.isDeleteExecutiveSummaryFile = isDeleteExecutiveSummaryFile;
  }

  @action
  setScrubbingPassedModalVisible(isVisible: boolean): void {
    this.isScrubbingPassedModalVisible = isVisible;
  }

  @action
  setScrubbingDisqualifiedModalVisible(isVisible: boolean): void {
    this.isScrubbingDisqualifiedModalVisible = isVisible;
  }

  @action
  setNominationForStaff(nominationForStaff: NominationForStaff): void {
    const nomination = cloneDeep(nominationForStaff.nomination);

    if (nomination.organization && !nomination.organization.regionalCeoFullName) {
      nomination.organization.regionalCeoFullName = undefined;
    }

    if (nomination.organization && !nomination.organization.regionalCeoEmail) {
      nomination.organization.regionalCeoEmail = undefined;
    }
    
    nomination.jointNominees.forEach((jointNominee) => {
      if (!jointNominee.regionalCeoFullName) jointNominee.regionalCeoFullName = undefined;
      if (!jointNominee.regionalCeoEmail) jointNominee.regionalCeoEmail = undefined;
    });

    this.nominationForStaff.nominationId = nominationForStaff.nominationId;
    this.nominationForStaff.isCaseStudy = nominationForStaff.isCaseStudy;
    this.nominationForStaff.isJudged = nominationForStaff.isJudged;
    this.nominationForStaff.qualifications = nominationForStaff.qualifications;
    this.nominationForStaff.nomination = nomination;
  }

  @action
  setMarketplaceOfferUrl(marketplaceOfferUrl?: string): void {
    this.nominationForStaff.nomination.marketplaceOfferUrl = marketplaceOfferUrl === 'https://' ? undefined : marketplaceOfferUrl;
  }

  @action
  setIsCaseStudy(isCaseStudy: boolean): void {
    this.nominationForStaff.isCaseStudy = isCaseStudy;
  }

  @action
  setIsEditingMode(isEditing: boolean): void {
    this.isEditingMode = isEditing;

    if (isEditing) {
      const initialOrganizationValue: Organization = {
        organizationId: 0,
        mpnId: '',
        nominationId: this.nominationForStaff.nomination.nominationId,
        companyName: '',
        address1: '',
        address2: '',
        city: '',
        stateProvince: '',
        postalCode: '',
        country: {
          countryId: 0,
          countryCode: '',
          displayName: '',
          postalCodeRequired: false,
        },
        phoneNumber: '',
        websiteUrl: '',
        prefix: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        organizationTypeId: OrganizationType.Main,
      };

      if (!this.nominationForStaff.nomination.organization) {
        this.nominationForStaffOrganization = initialOrganizationValue;
        return;
      }

      this.nominationForStaffOrganization = clone(this.nominationForStaff.nomination.organization);
    } else {
      this.nominationForStaffOrganization = undefined;
    }
  }

  @action
  setIsBeingValidated(isBeingValidated: boolean): void {
    this.isBeingValidated = isBeingValidated;
  }

  @computed
  get isNominationValidationMessageVisible(): boolean {
    return this.isEditingMode && this.isBeingValidated;
  }

  @action
  setOrganizationBodyRequest<K extends keyof Organization>(key: K, value: Organization[K]): void {
    if (!this.nominationForStaffOrganization) return;

    this.nominationForStaffOrganization[key] = value;
  }

  @action
  setOwnerBodyRequest<K extends keyof Owner>(key: K, value: Owner[K]): void {
    this.nominationForStaff.nomination.owner[key] = value;
  }

  @computed
  get isCountryAward(): boolean {
    return this.selectedAward?.isCountryAward ?? false;
  }

  @computed
  get hasExecutiveSummaryDocument(): boolean {
    return this.executiveSummaryDocument !== undefined;
  }

  @computed
  get activeSortedNominationRefs(): NominationRef[] {
    return this.isCountryAward
      ? this.sortedCountryNominationRefs
      : this.sortedAwardNominationRefs;
  }

  @computed
  get currentNominationIndex(): number {
    const currentId = this.nominationForStaff.nominationId;
    return this.activeSortedNominationRefs.findIndex(
      ref => ref.nominationId === currentId,
    );
  }

  @computed
  get nextNominationId(): number | undefined {
    const refs = this.activeSortedNominationRefs;
    if (refs.length === 0) return undefined;

    const index = this.currentNominationIndex;
    if (index < 0) return undefined;

    const nextIndex = (index + 1) % refs.length;
    return refs[nextIndex].nominationId;
  }

  @computed
  get maxJudgementCount(): number {
    return this.selectedAward?.maxJudgementCount ?? 0;
  }

  @action
  setWrittenExecutiveSummary(content?: string): void {
    this.writtenExecutiveSummaryContent = content;
  }

  @action
  setExecutiveSummaryDocument(documentMetadata?: DocumentMetadata): void {
    this.executiveSummaryDocument = documentMetadata;
  }

  @action
  setNominationSubmissionExpanded(isExpanded: boolean): void {
    this.isNominationSubmissionExpanded = isExpanded;
  }

  @computed
  get isWrittenExecutiveSummaryCollapsible(): boolean {
    return (this.writtenExecutiveSummaryContent ?? '').length > MAX_COLLAPSED_WRITTEN_EXECUTIVE_SUMMARY_LENGTH;
  }

  @computed
  get displayedWrittenExecutiveSummary(): string {
    const content = this.writtenExecutiveSummaryContent ?? '';
    return this.isNominationSubmissionExpanded || !this.isWrittenExecutiveSummaryCollapsible ?
      content :
      `${content.substring(0, MAX_COLLAPSED_WRITTEN_EXECUTIVE_SUMMARY_LENGTH)}...`;
  }

  @action
  setOtherPartnerTypeDescription(value: string, otherPartnerTypeId?: number): void {
    if (otherPartnerTypeId === undefined) return;
    const target = this.nominationForStaff.nomination.partnerTypes.find((type) => type.partnerTypeId === otherPartnerTypeId);
    if (target) {
      target.description = value;
    }
  }

  getValidationInput(otherPartnerTypeId?: number): NominationValidationInput | undefined {
    if (!this.nominationForStaffOrganization) return undefined;

    return {
      nomination: this.nominationForStaff.nomination,
      organization: this.nominationForStaffOrganization,
      editingAssociatedCountries: this.nominationForStaff.nomination.associatedCountries,
      otherPartnerTypeId,
      isCountryAward: this.isCountryAward,
      organizationPhoneCountryCode: this.countryCodeMapById.get(PhoneNumberField.Organization),
      requiresWrittenNomination: this.nominationForStaff.nomination.award.requireWrittenNomination,
      writtenNominationContent: this.writtenExecutiveSummaryContent ?? '',
      hasExecutiveSummaryDocument: this.hasExecutiveSummaryDocument,
    };
  }

  @computed
  get yourInformationValidation(): ValidationState<YourInformationValidation> {
    return createValidationState(ownerValidationSchema.safeParse(this.nominationForStaff.nomination.owner));
  }

  @computed
  get nominationOwnerEmailValidation(): ValidationState<NominationOwnerEmailValidation> {
    return createValidationState(nominationOwnerEmailValidationSchema.safeParse(
      this.nominationForStaff.nomination.owner,
    ));
  }

  getCompanyInformationValidation(otherPartnerTypeId?: number): ValidationState<CompanyInformationValidation> {
    return createValidationState(
      companyInformationValidationSchema.safeParse(this.getValidationInput(otherPartnerTypeId)),
    );
  }

  @computed
  get additionalContactValidations(): ValidationState<AdditionalContactValidation>[] {
    return this.nominationForStaff.nomination.contacts.map((contact) =>
      createValidationState(additionalContactValidationSchema.safeParse(contact)));
  }

  @computed
  get jointNomineeValidations(): ValidationState<JointNomineeValidation>[] {
    return this.nominationForStaff.nomination.jointNominees.map((jointNominee) =>
      createValidationState(jointNomineeValidationSchema.safeParse(jointNominee)));
  }

  @computed
  get nominationSubmissionValidation(): ValidationState<NominationSubmissionValidation> {
    return createValidationState(
      nominationSubmissionValidationSchema.safeParse(this.getValidationInput()),
    );
  }

  @computed
  get customerReferenceValidations(): ValidationState<CustomerReferenceValidation>[] {
    return this.nominationForStaff.nomination.customerReferences.map((customerReference) =>
      createValidationState(customerReferenceValidationSchema.safeParse(customerReference)));
  }

  isNominationValid(otherPartnerTypeId?: number): boolean {
    return this.yourInformationValidation.isValid
      && this.nominationOwnerEmailValidation.isValid
      && this.getCompanyInformationValidation(otherPartnerTypeId).isValid
      && this.additionalContactValidations.every((validation) => validation.isValid)
      && this.jointNomineeValidations.every((validation) => validation.isValid)
      && this.nominationSubmissionValidation.isValid;
  }

  @computed
  get partnerTypeRequiredValidationMessages(): string[] {
    if (!this.isEditingMode || this.nominationForStaff.nomination.partnerTypes.length > 0) return [];
    return this.getCompanyInformationValidation().fieldErrors.partnerTypes ?? [];
  }

  @computed
  get editingCountryIds(): number[] {
    return this.editingAssociatedCountries.map(country => country.countryId);
  }

  @computed
  get associatedCountryDisplayNames(): string {
    if (!this.nominationForStaff) return '';
    return this.nominationForStaff.nomination.associatedCountries.map(country => country.displayName).join(', ');
  }

  @action
  resetAssignCountriesModalStates(): void {
    this.editingAssociatedCountries = [];
  }

  @action
  setEditAssociatedCountryModalType(modalType?: ModalType): void {
    this.editAssociatedCountryModalType = modalType;
  }

  @action
  createEditAssociatedCountryRequestBodyForUpdate(nomination: Nomination): void {
    this.editingAssociatedCountries = clone(nomination.associatedCountries);
  }

  @computed
  get associatedCountriesRequestValidation(): ValidationState<AssociatedCountriesRequest> {
    return createValidationState(associatedCountriesRequestSchema.safeParse({
      associatedCountries: this.editingAssociatedCountries,
    }));
  }
}
